import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import * as state from '@/components/Analysis/state';
import { abyssColors, POI_STATUS_COLOR_MAP } from '@/theme/colors';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { useWizard } from '../context';
import { PanelHeader } from '../styles';
import { CardIcon, ReviewCard, ReviewContainer } from './styles';
import { useUploadLegacyEsSheetMutation } from '@/__generated__/graphql';
import { processEsSheetData, computeFinalData } from '../shared/utils';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { snackbarMessage } from '@/state';
import { useRouter } from '@/hooks';
import { useState } from 'react';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { PageLoader } from '@/components/shared/PageLoader';

type StatCardProps = {
  icon: string;
  iconBgColor: string;
  title: string;
  value: string | number;
  subtitle: string;
};

const StatCard = ({ icon, iconBgColor, title, value, subtitle }: StatCardProps) => {
  return (
    <ReviewCard>
      <Stack direction="row" alignItems="center" gap="0.8rem">
        <CardIcon backgroundColor={iconBgColor}>
          <AbyssIcon iconName={icon} size="md" />
        </CardIcon>
        <Typography variant="body2" fontWeight={500} color="primary.500">
          {title}
        </Typography>
      </Stack>
      <Stack gap="0.4rem">
        <Typography variant="h1" color="primary.500">
          {value}
        </Typography>
        <Typography variant="body2" fontWeight={400} color="primary.400">
          {subtitle}
        </Typography>
      </Stack>
    </ReviewCard>
  );
};

export const Review = () => {
  const editableEsSheet = useAtomValue(state.editableEsSheet);
  const structureId = useGetStructureId();
  const router = useRouter();
  const setSnackbarMessage = useSetAtom(snackbarMessage);
  const { goToPreviousStep } = useWizard();
  const [isImporting, setIsImporting] = useState(false);

  const [uploadLegacyEsSheet] = useUploadLegacyEsSheetMutation();

  if (!editableEsSheet) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          No data available. Please upload a file first.
        </Typography>
      </Box>
    );
  }

  const totalRows = editableEsSheet.data.length;
  const excludedCount = editableEsSheet.excludedRows?.length || 0;
  const inlineEditsCount = Object.keys(editableEsSheet.inlineEdits || {}).length;
  const toImport = totalRows - excludedCount;
  const totalUpdates = inlineEditsCount;
  const toSkip = excludedCount;

  const handleConfirmImport = async () => {
    if (!editableEsSheet || !structureId) return;

    processEsSheetData(editableEsSheet, true);
    const finalData = computeFinalData(editableEsSheet);
    setIsImporting(true);

    try {
      await uploadLegacyEsSheet({
        variables: {
          input: {
            headers: editableEsSheet.headers,
            structureId,
            originalData: editableEsSheet.data,
            columnMappings: editableEsSheet.mappings,
            valueMappings: editableEsSheet.valueMappings,
            inlineEdits: editableEsSheet.inlineEdits,
            excludedRows: editableEsSheet.excludedRows,
            finalData: finalData,
          },
        },
      });

      setSnackbarMessage({
        shouldShow: true,
        content: <Alert severity="success">ES sheet uploaded successfully</Alert>,
      });
      router.push(`/analysis/${structureId}/risk`);
    } catch {
      setSnackbarMessage({
        shouldShow: true,
        content: <Alert severity="error">ES sheet upload failed</Alert>,
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleCloseImportDialog = () => {
    setIsImporting(false);
  };

  return (
    <Stack width="100%" minWidth="85.4rem" paddingBottom="6.4rem" gap="3.2rem">
      <GenericDialog
        open={isImporting}
        onClose={handleCloseImportDialog}
        title=""
        paperStyles={{ maxWidth: '63.8rem', height: '22.2rem' }}
        contentStyles={{ padding: 0 }}
        actionsStyles={{ padding: 0 }}
        content={
          <Stack direction="row" alignItems="center" justifyContent="center" height="100%">
            <Stack direction="column" gap="1.6rem">
              <PageLoader variant="lg" flashing hideProgress />
              <Typography variant="h6" fontWeight={400} color="primary.500">
                This may take a few seconds.
              </Typography>
            </Stack>
          </Stack>
        }
        actions={[]}
      />
      <PanelHeader>
        <Typography variant="h3" fontWeight={500} color="primary.600">
          Review & Confirm Import
        </Typography>
        <Typography variant="h5" fontWeight={400} color="primary.400">
          Review the import summary and confirm to proceed
        </Typography>
      </PanelHeader>

      <ReviewContainer>
        <StatCard
          icon="modern-import"
          iconBgColor={abyssColors.success[400]}
          title="To import"
          value={toImport.toLocaleString()}
          subtitle="New equipment tags will be imported"
        />

        <StatCard
          icon="repeat-all"
          iconBgColor={abyssColors.primary[400]}
          title="Total updates"
          value={totalUpdates.toString().padStart(2, '0')}
          subtitle="Existing items will be updated"
        />

        <StatCard
          icon="skip-forward"
          iconBgColor={POI_STATUS_COLOR_MAP.INPROGRESS}
          title="To skip"
          value={toSkip.toString().padStart(2, '0')}
          subtitle="Rows will be skipped"
        />
      </ReviewContainer>

      <Stack direction="row" justifyContent="flex-end" alignItems="center" width="100%" gap={2}>
        <Button
          variant="outlined"
          onClick={goToPreviousStep}
          sx={{
            textTransform: 'none',
            borderColor: abyssColors.primary[300],
            color: abyssColors.primary[600],
            '&:hover': {
              borderColor: abyssColors.primary[400],
              backgroundColor: abyssColors.primary[50],
            },
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmImport}
          disabled={isImporting}
          sx={{
            textTransform: 'none',
            backgroundColor: abyssColors.success[600],
            '&:hover': {
              backgroundColor: abyssColors.success[700],
            },
          }}
        >
          Confirm import
        </Button>
      </Stack>
    </Stack>
  );
};
